// The generated review: every answered visible field, editable in place —
// how a family double-checks details before they are used. Consecutive
// screens sharing a section collapse under one section header.

import React from "react";

import { V2ResolvedField, V2ResolvedScreen } from "./types";

interface ReviewListProps {
  screens: V2ResolvedScreen[];
  fieldVisible: (field: V2ResolvedField) => boolean;
  values: Record<string, unknown>;
  /** Fields whose answers went stale under a context change — re-asked. */
  needsAnotherLook?: Set<string>;
  interpolate: (input: string) => string;
  onEdit: (screenIndex: number) => void;
}

interface SectionGroup {
  section: string | null;
  screens: { screen: V2ResolvedScreen; index: number }[];
}

/** Consecutive screens sharing a section collapse under one header. */
function groupBySection(
  screens: V2ResolvedScreen[],
  interpolate: (input: string) => string,
): SectionGroup[] {
  const groups: SectionGroup[] = [];
  screens.forEach((screen, index) => {
    const raw = screen.variant.properties.section;
    const section = raw ? interpolate(raw) : null;
    const open = groups[groups.length - 1];
    if (open && open.section === section) {
      open.screens.push({ screen, index });
      return;
    }
    groups.push({ section, screens: [{ screen, index }] });
  });
  return groups;
}

function part(value: unknown, key: string): string {
  const record = value as Record<string, unknown>;
  const raw = record[key];
  return typeof raw === "string" ? raw.trim() : "";
}

function displayFullName(value: unknown): string {
  return ["first_name", "middle_name", "last_name", "suffix"]
    .map((key) => part(value, key))
    .filter(Boolean)
    .join(" ");
}

function displayAddress(value: unknown): string {
  const streetLine = [
    part(value, "address_line_1"),
    part(value, "address_line_2"),
  ]
    .filter(Boolean)
    .join(" ");
  const stateZip = [part(value, "state"), part(value, "zip")]
    .filter(Boolean)
    .join(" ");
  return [streetLine, part(value, "city"), stateZip].filter(Boolean).join(", ");
}

function displayValue(field: V2ResolvedField, value: unknown): string {
  if (value === undefined || value === null || value === "") return "—";
  if (field.type === "yes_no") return value === true ? "Yes" : "No";
  if (field.type === "full_name") return displayFullName(value) || "—";
  if (field.type === "location") return displayAddress(value) || "—";
  if (Array.isArray(value)) return value.map(String).join(", ");
  if (typeof value === "object") {
    return Object.values(value as Record<string, unknown>)
      .filter((piece) => typeof piece === "string" && piece.length > 0)
      .join(" ");
  }
  const choices = field.variant.field_properties.choices;
  if (choices) {
    const match = choices.find((choice) => choice.ref === value);
    if (match) return match.label;
  }
  return String(value);
}

export default function ReviewList({
  screens,
  fieldVisible,
  values,
  needsAnotherLook,
  interpolate,
  onEdit,
}: ReviewListProps) {
  // Judged against what is on screen right now — a server progress snapshot
  // ages the moment another answer lands.
  const isMissing = (field: V2ResolvedField) =>
    field.variant.validations.required === true &&
    displayValue(field, values[field.ref]) === "—";
  const groups = groupBySection(screens, interpolate);
  return (
    <div className="flex flex-col gap-6">
      {groups.map((group) => (
        <div
          key={`${group.section ?? "unsectioned"}-${group.screens[0].index}`}
        >
          {group.section && (
            <h2 className="mb-2 text-[13px] font-bold uppercase tracking-[0.12em] text-oat-800">
              {group.section}
            </h2>
          )}
          <div className="divide-y divide-oat-100 rounded-xl border border-oat-200 bg-white px-5">
            {group.screens.map(({ screen, index }) =>
              screen.fields
                .filter(
                  (field) =>
                    field.type !== "section_heading" && fieldVisible(field),
                )
                .map((field) => {
                  const missing = isMissing(field);
                  return (
                    <div
                      key={field.variant.id}
                      className="flex items-start justify-between gap-4 py-4"
                    >
                      <div>
                        {needsAnotherLook?.has(field.ref) && (
                          <span className="mb-1 inline-block rounded-full bg-thistle/25 px-2.5 py-0.5 text-xs font-bold text-soil">
                            Needs another look
                          </span>
                        )}
                        <p
                          className={
                            missing
                              ? "text-sm font-bold text-red-700"
                              : "text-sm font-bold text-oat-700"
                          }
                        >
                          {interpolate(field.variant.title)}
                        </p>
                        <p
                          className={
                            missing
                              ? "ph-no-capture font-bold text-red-700"
                              : "ph-no-capture text-soil"
                          }
                        >
                          {displayValue(field, values[field.ref])}
                        </p>
                        {missing && (
                          <p className="mt-0.5 text-sm font-medium text-red-700">
                            This is required.
                          </p>
                        )}
                      </div>
                      <button
                        type="button"
                        className="shrink-0 font-bold text-soil underline underline-offset-4 hover:text-soil/70"
                        onClick={() => onEdit(index)}
                      >
                        Edit
                      </button>
                    </div>
                  );
                }),
            )}
          </div>
        </div>
      ))}
    </div>
  );
}
